import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Form, required, useLogin, useNotify } from "ra-core";
import type { SubmitHandler, FieldValues } from "react-hook-form";
import { Link, useLocation, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { EmailInput } from "@/components/admin/email-input";
import { TextInput } from "@/components/admin/text-input";
import { Notification } from "@/components/admin/notification";
import { useConfigurationContext } from "@/components/atomic-crm/root/ConfigurationContext.tsx";
import { LoginHeroPanel } from "@/components/atomic-crm/login/LoginHeroPanel";
import type { CrmDataProvider } from "../providers/types";
import { SSOAuthButton } from "./SSOAuthButton";
import { useDataProvider } from "ra-core";
import { LoginSkeleton } from "./LoginSkeleton";

/**
 * Login page displayed when authentication is enabled and the user is not authenticated.
 *
 * Automatically shown when an unauthenticated user tries to access a protected route.
 * Handles login via authProvider.login() and displays error notifications on failure.
 *
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/loginpage LoginPage documentation}
 * @see {@link https://marmelab.com/shadcn-admin-kit/docs/security Security documentation}
 */
export const LoginPage = (props: { redirectTo?: string }) => {
  const {
    darkModeLogo,
    title,
    googleWorkplaceDomain,
    disableEmailPasswordAuthentication,
  } = useConfigurationContext();
  const { redirectTo } = props;
  const [loading, setLoading] = useState(false);
  const hasDisplayedRecoveryNotification = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const login = useLogin();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const { data: isInitialized, isPending } = useQuery({
    queryKey: ["init"],
    queryFn: async () => {
      return dataProvider.isInitialized();
    },
  });

  // Tras un logout, react-admin pone en `state` el path previo (nextPathname) para
  // redirigir al usuario tras /login. Si estuvo en la consola `/sas/...`, `useLogin` le
  // devolvería a SAS en vez del CRM; al entrar por /login (dueños/equipo) queremos /.
  useLayoutEffect(() => {
    const path = location.pathname.replace(/\/$/, "") || "/";
    if (!path.endsWith("/login")) {
      return;
    }
    const st = location.state as
      | { nextPathname?: string; nextSearch?: string }
      | null
      | undefined;
    const next = st?.nextPathname;
    if (
      typeof next !== "string" ||
      (next !== "/sas" && !next.startsWith("/sas/"))
    ) {
      return;
    }
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      {
        replace: true,
        state: { ...st, nextPathname: undefined, nextSearch: undefined },
      },
    );
  }, [
    location.pathname,
    location.search,
    location.hash,
    location.state,
    navigate,
  ]);

  // Con router por *historia* la ruta es solo /login. Quitar fragmentos #/… heredados del
  // HashRouter (marcadores, SW, caché) — p. ej. /login#/login. No tocar #access_token=…
  // ni otros hashes con carga útil.
  useLayoutEffect(() => {
    const path = location.pathname.replace(/\/$/, "") || "/";
    if (path !== "/login" || !location.hash) {
      return;
    }
    if (location.hash.includes("=")) {
      return;
    }
    const h = location.hash;
    const isLegacyAppRouteHash =
      h === "#/login" || h.startsWith("#/login/") || h === "#/" || h === "#";
    if (!isLegacyAppRouteHash) {
      return;
    }
    navigate(
      { pathname: location.pathname, search: location.search, hash: "" },
      { replace: true },
    );
  }, [location.hash, location.pathname, location.search, navigate]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const shouldNotify = searchParams.get("passwordRecoveryEmailSent") === "1";

    if (!shouldNotify || hasDisplayedRecoveryNotification.current) {
      return;
    }

    hasDisplayedRecoveryNotification.current = true;
    notify(
      "If you're a registered user, you should receive a password recovery email shortly.",
      {
        type: "success",
      },
    );

    searchParams.delete("passwordRecoveryEmailSent");
    const nextSearch = searchParams.toString();
    navigate(
      {
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      },
      { replace: true },
    );
  }, [location.pathname, location.search, navigate, notify]);

  const handleSubmit: SubmitHandler<FieldValues> = (values) => {
    setLoading(true);
    login(values, redirectTo)
      .then(() => {
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "error",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      });
  };

  if (isPending) {
    return <LoginSkeleton />;
  }

  return (
    <div className="min-h-screen flex">
      <div className="relative grid w-full lg:grid-cols-2">
        <LoginHeroPanel logoSrc={darkModeLogo} title={title} />
        <div className="flex flex-col justify-center w-full p-4 lg:p-8">
          <div className="w-full space-y-6 lg:mx-auto lg:w-[350px]">
            <div className="text-center space-y-1">
              <h1 className="text-2xl font-semibold tracking-tight">Sign in</h1>
            </div>
            {!isInitialized && !disableEmailPasswordAuthentication ? (
              <div className="space-y-2 rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground">
                <p>
                  This workspace is invite-only. Ask your administrator to add
                  you from Settings → Users.
                </p>
              </div>
            ) : null}
            {disableEmailPasswordAuthentication ? null : (
              <Form className="space-y-8" onSubmit={handleSubmit}>
                <EmailInput
                  label="Email"
                  source="email"
                  type="email"
                  validate={required()}
                />
                <TextInput
                  label="Password"
                  source="password"
                  type="password"
                  validate={required()}
                />
                <div className="flex flex-col gap-4">
                  <Button
                    type="submit"
                    className="cursor-pointer"
                    disabled={loading}
                  >
                    Sign in
                  </Button>
                </div>
              </Form>
            )}
            {googleWorkplaceDomain ? (
              <SSOAuthButton className="w-full" domain={googleWorkplaceDomain}>
                Sign in with Google Workplace
              </SSOAuthButton>
            ) : null}
            {disableEmailPasswordAuthentication ? null : (
              <Link
                to={"/forgot-password"}
                className="block text-sm text-center hover:underline"
              >
                Forgot your password?
              </Link>
            )}
          </div>
        </div>
      </div>
      <Notification />
    </div>
  );
};
